import { useMemo, useState } from 'react';
import { Rocket, Check, ChevronRight, X } from 'lucide-react';
import { useTeamStats } from '@/hooks/useTeamStats';
import { useLaborPrices } from '@/hooks/useSettings';

interface NextStepsCardProps {
  workshopId: string | null;
  hasSlots: boolean;
  hasFirstOS: boolean;
}

interface Step {
  key: string;
  label: string;
  done: boolean;
  href: string;
  cta: string;
}

/**
 * Checklist persistente de próximos passos no dashboard — continuidade do
 * onboarding após o wizard. Some quando tudo está concluído ou quando o
 * gestor dispensa (lembrado por oficina via localStorage).
 */
export function NextStepsCard({ workshopId, hasSlots, hasFirstOS }: NextStepsCardProps) {
  const month = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const { data: team } = useTeamStats(month);
  const { prices } = useLaborPrices();

  const dismissKey = `mustauto:nextsteps_dismissed:${workshopId ?? 'none'}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === 'true'; } catch { return false; }
  });

  const steps: Step[] = [
    { key: 'workshop', label: 'Dados da oficina', done: true, href: '/settings', cta: 'Editar' },
    { key: 'team', label: 'Convidar sua equipe', done: (team?.mechanics.length ?? 0) > 0, href: '/team', cta: 'Convidar' },
    { key: 'prices', label: 'Cadastrar tabela de preços', done: prices.some((p) => !p.pendingConfirmation), href: '/settings', cta: 'Cadastrar' },
    { key: 'slots', label: 'Cadastrar vagas / elevadores', done: hasSlots, href: '/slots', cta: 'Cadastrar' },
    { key: 'os', label: 'Abrir sua primeira OS', done: hasFirstOS, href: '/service-orders/new', cta: 'Abrir' },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  if (dismissed || allDone) return null;

  function dismiss() {
    setDismissed(true);
    try { localStorage.setItem(dismissKey, 'true'); } catch { /* ignore */ }
  }

  return (
    <div className="rounded-xl border border-primary/30 border-l-4 border-l-primary bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Rocket size={18} className="text-primary" />
          <h2 className="text-base font-bold">Comece por aqui</h2>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          aria-label="Dispensar checklist"
        >
          dispensar <X size={13} />
        </button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Sua oficina está quase no ponto — faltam {steps.length - doneCount} de {steps.length} passos.
      </p>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      <ul className="mt-4 space-y-2.5">
        {steps.map((step) => (
          <li key={step.key} className="flex items-center gap-3">
            <span
              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
                step.done ? 'border-success bg-success text-white' : 'border-border'
              }`}
            >
              {step.done && <Check size={12} strokeWidth={3} />}
            </span>
            <span className={`flex-1 text-sm ${step.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {step.label}
            </span>
            {!step.done && (
              <a
                href={step.href}
                className="flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline"
              >
                {step.cta} <ChevronRight size={13} />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
